// ============================================================
// BarTimeSigControl
// ============================================================
// A hover control that appears on any bar, allowing the user
// to set a per-bar time signature override.
// Shows as a small badge (e.g. "3/4") when an override is set,
// or a faint "4/4" when inheriting from the song.
// Click opens a small popover with time signature options.
// ============================================================

import React, { useState, useRef, useEffect, useCallback } from 'react';
import type { TimeSignature } from '../../core/types';
import { useSongStore } from '../../store/songStore';

// ------------------------------------------------------------
// TIME SIGNATURES
// ------------------------------------------------------------

const TIME_SIGNATURES: TimeSignature[] = [
  { numerator: 4, denominator: 4 },
  { numerator: 3, denominator: 4 },
  { numerator: 2, denominator: 4 },
  { numerator: 6, denominator: 8 },
  { numerator: 9, denominator: 8 },
  { numerator: 5, denominator: 4 },
  { numerator: 7, denominator: 8 },
];

function tsLabel(ts: TimeSignature): string {
  return `${ts.numerator}/${ts.denominator}`;
}

function tsEqual(a: TimeSignature, b: TimeSignature): boolean {
  return a.numerator === b.numerator && a.denominator === b.denominator;
}

// ------------------------------------------------------------
// PROPS
// ------------------------------------------------------------

interface BarTimeSigControlProps {
  sectionId: string;
  barId: string;
  /** The bar's own override (null = inheriting) */
  barTimeSignature: TimeSignature | null;
  /** The effective time signature (bar override or song default) */
  effectiveTs: TimeSignature;
  /** Whether the bar is hovered (control only shows on hover) */
  visible: boolean;
}

// ------------------------------------------------------------
// COMPONENT
// ------------------------------------------------------------

export function BarTimeSigControl({
  sectionId,
  barId,
  barTimeSignature,
  effectiveTs,
  visible,
}: BarTimeSigControlProps) {
  const setBarTimeSignature = useSongStore((s) => s.setBarTimeSignature);
  const [open, setOpen]     = useState(false);
  const ref                 = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handle = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  const handleSelect = useCallback((ts: TimeSignature) => {
    const isSameAsEffective = tsEqual(ts, effectiveTs) && barTimeSignature === null;
    // If selecting the currently inherited value, no-op
    // If selecting the current override, remove it (reset to inherit)
    if (barTimeSignature && tsEqual(ts, barTimeSignature)) {
      setBarTimeSignature(sectionId, barId, null);
    } else {
      setBarTimeSignature(sectionId, barId, ts);
    }
    setOpen(false);
  }, [sectionId, barId, barTimeSignature, effectiveTs, setBarTimeSignature]);

  const hasOverride = barTimeSignature !== null;

  if (!visible && !open) return null;

  return (
    <div
      ref={ref}
      className="bar-timesig-control"
      style={{
        position: 'absolute',
        bottom: 4,
        left: 6,
        zIndex: 10,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Badge / trigger */}
      <button
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        title={hasOverride ? 'Bar time signature override — click to change' : 'Click to set bar time signature'}
        style={{
          fontSize: 9,
          fontFamily: 'var(--font-mono)',
          color: hasOverride ? 'var(--color-accent)' : 'var(--color-ink-muted)',
          opacity: hasOverride ? 1 : 0.6,
          background: hasOverride ? 'rgba(var(--color-accent-rgb), 0.08)' : 'transparent',
          border: hasOverride ? '1px solid var(--color-accent)' : '1px solid transparent',
          borderRadius: 3,
          cursor: 'pointer',
          padding: '1px 4px',
          lineHeight: 1.4,
          letterSpacing: '0.03em',
        }}
      >
        {tsLabel(effectiveTs)}
      </button>

      {/* Popover */}
      {open && (
        <div
          className="dropdown"
          style={{
            position: 'absolute',
            bottom: '100%',
            left: 0,
            marginBottom: 4,
            padding: 6,
            minWidth: 80,
            zIndex: 200,
          }}
        >
          <div style={{
            fontSize: 9,
            fontFamily: 'var(--font-label)',
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            color: 'var(--color-ink-muted)',
            marginBottom: 4,
            paddingLeft: 2,
          }}>
            Bar time sig
          </div>

          {TIME_SIGNATURES.map((ts) => {
            const isActive = barTimeSignature && tsEqual(ts, barTimeSignature);
            const isInherited = !barTimeSignature && tsEqual(ts, effectiveTs);
            return (
              <button
                key={tsLabel(ts)}
                onClick={() => handleSelect(ts)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '3px 8px',
                  fontSize: 11,
                  fontFamily: 'var(--font-mono)',
                  borderRadius: 3,
                  border: 'none',
                  cursor: 'pointer',
                  background: isActive
                    ? 'var(--color-accent)'
                    : 'transparent',
                  color: isActive
                    ? 'white'
                    : isInherited
                    ? 'var(--color-ink-secondary)'
                    : 'var(--color-ink-muted)',
                  fontWeight: isInherited && !isActive ? 600 : 400,
                }}
              >
                {tsLabel(ts)}
                {isInherited && !isActive && (
                  <span style={{ fontSize: 8, marginLeft: 4, opacity: 0.5 }}>default</span>
                )}
                {isActive && (
                  <span style={{ fontSize: 8, marginLeft: 4, opacity: 0.7 }}>✕ reset</span>
                )}
              </button>
            );
          })}

          {hasOverride && (
            <button
              onClick={() => { setBarTimeSignature(sectionId, barId, null); setOpen(false); }}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '3px 8px',
                marginTop: 4,
                fontSize: 10,
                fontFamily: 'var(--font-mono)',
                color: 'var(--color-error)',
                background: 'transparent',
                border: 'none',
                borderTop: '1px solid var(--color-rule)',
                cursor: 'pointer',
                paddingTop: 6,
              }}
            >
              ✕ Remove override
            </button>
          )}
        </div>
      )}
    </div>
  );
}